import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Select } from 'antd';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import useAppointmentMeta from '@components/Clinicians/Session/hooks/useAppointmentMeta';
import { SessionFormItem } from '@components/Clinicians/Session/Components/SessionFormItem';
import { type ApptActionCodes } from 'vl-common/src/schemas/getMetaAppointments';
import { useSessionAction } from '@hooks/useSessionAction';

export const OTHER_OPTION = { label: 'Other', value: '_OTHER_REFERRAL_REASON_CODE_' } as const;

export const ReasonForReferralFormItem = ({
  referralOptions,
  actionCode,
  required = true,
  onChange = () => {}
}: {
  referralOptions?: any;
  actionCode: ApptActionCodes;
  required?: boolean;
  onChange?: (value: string) => void;
}) => {
  const { t } = useTranslation('common');
  const { appointment } = useSessionAppointment();
  const isNHS = appointment.patient_nhs_client;

  const { REASON_KEY, OTHER_REASON_KEY } = useSessionAction(actionCode);

  const [reasonCode, setReasonCode] = useSessionFormState(REASON_KEY);
  const [otherReason, setOtherReason] = useSessionFormState(OTHER_REASON_KEY);

  const { data: appointmentMeta } = useAppointmentMeta();

  const options =
    (referralOptions ||
      appointmentMeta?.referral_reasons
        ?.map(({ ref_reason_code: value, ref_reason_desc: label }) => ({
          value,
          label
        }))
        .concat(!isNHS ? OTHER_OPTION : [])) ??
    [];

  const handleSelect = (value: string) => {
    if (value !== OTHER_OPTION.value) {
      setOtherReason(undefined);
    }

    onChange?.(value);

    setReasonCode(value);
  };

  return (
    <>
      <SessionFormItem required={required} label={t('Reason for referral')} name={REASON_KEY}>
        <Select
          data-testid="reason-for-referral"
          options={options}
          onSelect={handleSelect}
          value={reasonCode}
          disabled={appointment.appointment_completed || appointment.read_only}
        />
      </SessionFormItem>

      {reasonCode === OTHER_OPTION.value && (
        <SessionFormItem required label={t('Specify other reason')} name={OTHER_REASON_KEY}>
          <Input
            onChange={(e) => {
              setOtherReason(e.target.value);
            }}
            value={otherReason}
            disabled={appointment.appointment_completed || appointment.read_only}
          />
        </SessionFormItem>
      )}
    </>
  );
};
